import React, {
  useEffect, useRef, useState, Fragment,
} from 'react';
import { useIntl } from 'react-intl';
import { Link as GatsbyLink } from 'gatsby';
import {
  Button, Link, Heading, Transition, Container, Panel,
} from '@printdeal/ui-library';
import { useSelector } from 'react-redux';
import useMediaQuery from '../../../hooks/useMediaQuery';
import LoginForm from '../../authentication/LoginForm';
import LostPasswordLink from '../../authentication/LostPasswordLink';
import useSystemPages from '../../../hooks/useSystemPages';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { dispatchDataLayerLoginPromptRegister, dispatchDataLayerLoginPromptView } from '../../../helpers/dataLayerHelpers/LoginPromptDataLayerHelper';
import { SESSION_STORAGE_KEYS } from '../../../../gatsby/constants';

import { selectors as authSelectors } from '../../../store/slices/authSlice';
import { cookieDialogWasShown } from '../../../helpers/CookiebotHelper';

interface LoginPromptProps {
  pagePath?: string
}

export const LoginPrompt = ({ pagePath = 'home' }: LoginPromptProps) => {
  const intl = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTracked, setIsTracked] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 992px)');
  const loginPromptRef = useRef(null);
  const [homePage, registrationPage] = useSystemPages('home', 'registration');
  const isLoggedIn = useSelector(authSelectors.isLoggedIn);
  const isAuthLoading = useSelector(authSelectors.isAuthLoading);
  const [cookieDialogShown, setCookieDialogShown] = useState<boolean | undefined>(undefined);

  const getHidePromptCurrentSession = () => sessionStorage[SESSION_STORAGE_KEYS.HIDE_LOGIN_PROMPT] !== undefined;

  const setHidePromptCurrentSession = () => {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.HIDE_LOGIN_PROMPT, 'true');
  };

  const closeModal = () => {
    setHidePromptCurrentSession();
    setIsModalOpen(false);
  };

  useClickOutside(loginPromptRef, closeModal);

  // TODO(consent): use `useConsent()` instead
  const handleInitialization = () => {
    if (getHidePromptCurrentSession() || !isDesktop || isLoggedIn || isAuthLoading) {
      return undefined;
    }

    if (!isTracked) {
      dispatchDataLayerLoginPromptView(pagePath);
      setIsTracked(true);
    }

    setIsModalOpen(true);

    return () => {};
  };

  // TODO: review this useEffect, it shouldn't be needed
  useEffect(() => {
    if (cookieDialogShown !== false) return undefined;

    const eventHandler = () => {
      setCookieDialogShown(true);

      return handleInitialization();
    };

    const events = ['CookiebotOnAccept', 'CookiebotOnDecline'];

    events.forEach((event) => {
      window.addEventListener(event, eventHandler, { once: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, eventHandler);
      });
    };
  }, [cookieDialogShown]);

  // TODO(consent): review this useEffect, it shouldn't handle cookie dialog
  useEffect(() => {
    if (!isDesktop) return undefined;

    if (!cookieDialogWasShown()) {
      setCookieDialogShown(false);
      return undefined;
    }

    return handleInitialization();
  }, [isTracked, isDesktop, isLoggedIn, isAuthLoading]);

  return (
    <Container className="pd-relative pd-z-[31]">
      <Transition
        show={isModalOpen}
        as={Fragment}
        variant="appear-from-top"
      >
        <Panel ref={loginPromptRef} className="pd-absolute pd-right-0 pd-top-16 pd-flex pd-w-80 pd-flex-col pd-gap-3" data-testid="login-prompt">
          <header className="pd-flex pd-items-center">
            <Heading as="h3">
              {intl.formatMessage({
                id: 'login.slideout.loginTitle',
                defaultMessage: 'Log in',
              })}
            </Heading>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              as="button"
              type="button"
              variant="secondary"
              className="pd-ml-auto pd-grow-0"
              onClick={closeModal}
              data-testid="login-prompt-close-button"
              iconBefore="times"
              screenReaderLabel={intl.formatMessage({
                id: 'account.general.modal.close',
                defaultMessage: 'Close',
              })}
            />
          </header>

          <LoginForm
            setIsModalOpen={(modalOpenState: boolean) => {
              if (modalOpenState === false) {
                setHidePromptCurrentSession();
              }
              setIsModalOpen(modalOpenState);
            }}
            useLoginSlideOut
            eventLabel="homepage"
            redirectUrlAfterLogin={homePage.path}
          />

          <LostPasswordLink eventLabel="homepage" />

          <hr />

          {registrationPage?.path && (
            <Button
              as={GatsbyLink}
              variant="secondary"
              className="pd-w-full"
              to={`${registrationPage.path}`}
              onClick={dispatchDataLayerLoginPromptRegister}
            >
              {intl.formatMessage({
                id: 'account.register',
                defaultMessage: 'Create Account',
              })}
            </Button>
          )}
        </Panel>
      </Transition>
    </Container>
  );
};
